import { AfterContentChecked, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';
import { NotificationService } from 'src/app/core/services/notification.service';
import { TRANSLATION_MANAGER_TYPE } from 'src/app/extensions/constants/commonConstants';
import { DictionaryAPIService } from 'src/app/extensions/services/api/dictionary-api.service';
import { LanguageAPIService } from 'src/app/extensions/services/api/language-api.service';
import { TenantAPIService } from 'src/app/extensions/services/api/tenant-api.service';
import { LoaderService } from 'src/app/extensions/services/loader.service';
import { isNotEmpty } from 'src/app/extensions/utilities';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-language-detail',
  templateUrl: './language-detail.component.html',
  styleUrls: ['./language-detail.component.scss'],
  standalone: false,
})
export class LanguageDetailComponent implements OnInit, AfterContentChecked {
  @ViewChild('form') public form: NgForm;

  languageId: string;
  entityType: string;

  refTranslation: app.models.DynamicObjectModel;
  targetTranslation: app.models.DynamicObjectModel = {};
  originalDbTranslation: app.models.DynamicObjectModel | null = null;

  language: app.models.Language;
  dictionaryName: string;

  dictionaryNotFound: boolean = false;
  dictionaryTranslationSubtitle: string = undefined;

  TRANSLATION_MANAGER_TYPE = TRANSLATION_MANAGER_TYPE;

  dataLoaded: boolean = false;

  uploadedFile: File;
  intialTargetTranslation: app.models.DynamicObjectModel = {};

  constructor(
    private _activatedRoute: ActivatedRoute,
    private _tenantAPIService: TenantAPIService,
    private _languageAPIService: LanguageAPIService,
    private _notificationService: NotificationService,
    private _translationService: TranslateService,
    private _cdref: ChangeDetectorRef,
    private _dictionaryAPIService: DictionaryAPIService,
    private _router: Router,
    private _loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    this.languageId = this._activatedRoute.snapshot.params['languageId'];
    this.entityType = this._activatedRoute.snapshot.params['entityType'];

    if (this.languageId && this.entityType) {
      this._languageAPIService.getLanguage(this.languageId).subscribe(languageResponse => {
        this.language = languageResponse;

        let source: Observable<any>;
        switch (this.entityType) {
          case TRANSLATION_MANAGER_TYPE.APP_FILES:
            source = forkJoin([
              this._languageAPIService.getTranslationFromAssetsByLanguageCode('en'),
              this._languageAPIService.getTranslationFromAssetsByLanguageCode(this.language.code),
              this._tenantAPIService.getTenantTranslation(this.language.code),
            ]);

            source.subscribe(([localRefTranslationFile, localTargetTranslationFile, dbTargetTranslationFile]) => {
              this.refTranslation = this.flattenObject(localRefTranslationFile);
              this.generateTargetTranslation(dbTargetTranslationFile, localTargetTranslationFile);
              this.dataLoaded = true;
            });
            break;
          default:
            if (!isNaN(Number(this.entityType))) {
              this._dictionaryAPIService
                .getDictionaryTranslation(this.entityType, this.language?.code)
                .subscribe(response => {
                  if (response) {
                    this.refTranslation = response.refTranslationData;
                    this.targetTranslation = response.translationData;
                    this.dictionaryName = response.dictionaryName;

                    this.dataLoaded = true;
                  }
                });
            } else {
              this.dictionaryNotFound = true;
            }
            break;
        }
      });
    }
  }

  ngAfterContentChecked() {
    this._cdref.detectChanges();

    this.dictionaryTranslationSubtitle = this._translationService.instant(
      'admin.languages.dictionary.languageDetailSubtitle'
    );
    this.dictionaryTranslationSubtitle = this.dictionaryTranslationSubtitle.replace(
      '<<dictionary_name>>',
      `<span class="text-primary fw-bold">${this.dictionaryName}</span>`
    );
  }

  generateTargetTranslation(
    dbTranslation: app.models.DynamicObjectModel,
    localTargetTranslation: app.models.DynamicObjectModel
  ) {
    if (dbTranslation == null) {
      this.targetTranslation = this.intialTargetTranslation = this.flattenObject(localTargetTranslation);
      return;
    }

    const localTargetTranslationFlatten = this.flattenObject(localTargetTranslation);
    let dbTargetTranslationFlatten = this.flattenObject(JSON.parse(dbTranslation!.data));

    this.originalDbTranslation = { ...dbTargetTranslationFlatten };

    Object.keys(localTargetTranslationFlatten).forEach(elementKey => {
      if (!isNotEmpty(dbTargetTranslationFlatten[elementKey])) {
        dbTargetTranslationFlatten[elementKey] = localTargetTranslationFlatten[elementKey];
      }
    });
    this.targetTranslation = this.intialTargetTranslation = dbTargetTranslationFlatten;
  }

  validateInputTranslation(input: string) {
    if (input == null) {
      return true;
    }
    if (this.form) {
      return !(this.form.submitted || isNotEmpty(input));
    }
  }

  isFormValid() {
    return this.form?.valid;
  }

  saveBtnHandler() {
    switch (this.entityType) {
      case TRANSLATION_MANAGER_TYPE.APP_FILES:
        this.saveTranslationFile();
        break;
      default:
        if (!isNaN(Number(this.entityType))) {
          this.saveTranslationDictionary();
        }
        break;
    }
  }

  saveTranslationFile() {
    const translationFileModel = {
      data: this.unflattenObject(this.targetTranslation),
      languageCode: this.language!.code,
      languageId: this.languageId,
    };

    this._languageAPIService.saveTranslationFile(translationFileModel).subscribe(response => {
      if (response.isSuccess) {
        this.form.resetForm(this.form.value);
        this._notificationService.showSuccess(
          this._translationService.instant('admin.languages.app.notification.fileSaved'),
          this._translationService.instant('common.success')
        );
        window.location.reload();
      } else {
        this._notificationService.showWarning(
          this._translationService.instant('common.somethingWentWrong'),
          this._translationService.instant('common.warning')
        );
      }
    });
  }

  saveTranslationDictionary() {
    let data = {
      dictionaryId: this.entityType,
      data: this.targetTranslation,
      language: this.language?.code,
    };

    this._dictionaryAPIService.saveDictionaryTranslation(data).subscribe(response => {
      if (response.isSuccess) {
        this.form.resetForm(this.form.value);
        this._notificationService.showSuccess(
          this._translationService.instant('admin.languages.dictionary.notification.translationSaved'),
          this._translationService.instant('common.success')
        );
      }
    });
  }

  selectDictionaryHandler() {
    this._router.navigate(['languages/select-dictionary']);
  }

  uploadTranslationFileHandler(event: any) {
    this._loaderService.isLoading.next(true);

    this.uploadedFile = event.target.files[0];
    const reader: FileReader = new FileReader();

    reader.onload = (e: any) => {
      const data = e.target.result;
      const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'binary' });
      const firstSheetName: string = workbook.SheetNames[0];
      const worksheet: XLSX.WorkSheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      jsonData.forEach((item: string[]) => {
        const translationKey: string = item[0];
        if (this.refTranslation.hasOwnProperty(translationKey)) {
          const translationValue: string = item[1];
          this.targetTranslation = {
            ...this.targetTranslation,
            [translationKey]: translationValue,
          };
        }
      });

      this._loaderService.isLoading.next(false);
    };

    reader.readAsArrayBuffer(this.uploadedFile);
  }

  deleteFileHandler(fileInput: HTMLInputElement) {
    fileInput.value = null;
    this.uploadedFile = null;
    this.targetTranslation = this.intialTargetTranslation;
  }

  exportToExcelHandler() {
    const rowAsJsonList = Object.entries(this.refTranslation).map(([key, _]) => {
      return { TranslationKey: key, [this.language.code.toUpperCase()]: this.targetTranslation[key] };
    });
    rowAsJsonList.sort((a, b) => a.TranslationKey.localeCompare(b.TranslationKey));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rowAsJsonList);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const blob: Blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${this.language.code}.xlsx`;
    link.click();

    URL.revokeObjectURL(link.href);
  }

  private flattenObject(ob: any) {
    let toReturn = {};

    for (var i in ob) {
      if (!ob.hasOwnProperty(i)) continue;

      if (typeof ob[i] == 'object' && ob[i] !== null) {
        let flatObject = this.flattenObject(ob[i]);
        for (var x in flatObject) {
          if (!flatObject.hasOwnProperty(x)) continue;

          toReturn[i + '.' + x] = flatObject[x];
        }
      } else {
        toReturn[i] = ob[i];
      }
    }
    return toReturn;
  }

  private unflattenObject(ob: any) {
    let result = {};
    for (var i in ob) {
      let keys = i.split('.');
      keys.reduce(function (r, e, j) {
        return r[e] || (r[e] = isNaN(Number(keys[j + 1])) ? (keys.length - 1 == j ? ob[i] : {}) : []);
      }, result);
    }
    return result;
  }
}
